import * as React from "react";
import { AlertMessage, AlertType, ButtonType, Checkbox, ConditionalCheckboxList, CustomButton, CustomSubmitButton, DatePickerRange, Dropdown, Form, HiddenInput, HsaIdInput, Modal, ModalBody, ModalFooter, ModalSize, MultiSelect, Select, TextInput, UnorderedList } from "soprasteria-ui-library";
import { ICheckboxListItem, IListItem } from "soprasteria-ui-library/dist/models/IFormInput";
import { SortOrder } from "soprasteria-ui-library/dist/models/IPagination";
import { useAuth } from "../../App";
import { ClinicApi } from "../../api/ClinicApi";
import { MunicipalityDistrictApi } from "../../api/MunicipalityDistrictApi";
import { OrganisationApi } from "../../api/OrganisationApi";
import { ResidenceApi } from "../../api/ResidenceApi";
import { UserApi } from "../../api/UserApi";
import { UserRoleGroupApi } from "../../api/UserRoleGroupApi";
import { getStartAndEndDateDisplayValue } from "../../helpers/DateHelper";
import { setMessage } from "../../helpers/MessageHelper";
import { emailAddressRegExp } from "../../helpers/RegExpHelper";
import { IMunicipalityDistrict } from "../../models/IMunicipalityDistrict";
import { IOrganisationClinic } from "../../models/IOrganisationClinic";
import { IResidenceSearchParams } from "../../models/IResidence";
import { IRoleRequest } from "../../models/IRole";
import { ISearchType } from "../../models/ISearchType";
import { IUser } from "../../models/IUser";
import { IUserRoleGroup } from "../../models/IUserRoleGroup";
import { LIBRETTOVÅRDGIVARE, MENUETTPORTAL, MENUETTPORTALKOMMUN, SYMFONIKOMMUN, SYMFONIVÅRDGIVARE } from "../../utils/ApplicationConstants";
import { UNKOWNDISTRICTCODE } from "../../utils/DistrictConstants";
import { ALLABOENDEN, SPECIFIKA_BOENDEN } from "../../utils/ResidenceGroupConstants";
import { SearchUser } from "../Search/SearchUser";
import { UserContext, isInRole } from "../../contexts/UserContext";
import { MENUETTPORTALKOMMUN_PRIVATANSVARIG, MENUETTPORTALKOMMUN_PRIVATANSVARIG_ROLEID } from "../../utils/RoleConstants";

interface IProps {
    userRoleGroup: IUserRoleGroup | undefined;
    userId?: number;
    clinicId?: number;
    organisationId?: number;
    canEdit: boolean;
    onSubmit: (newUserRoleGroup: boolean) => void;
    closeModal: () => void;
    openedFromUser?: boolean;
    openedFromClinic?: boolean;
    openedFromOrganisation?: boolean;
    openedFromMunicipality?: boolean;
}

export const RoleGroupModal: React.FunctionComponent<IProps> = ({ userRoleGroup, userId, clinicId, organisationId, canEdit, onSubmit, closeModal, openedFromUser = false, openedFromClinic = false, openedFromOrganisation = false, openedFromMunicipality = false }) => {
    const auth = useAuth();
    const api = new UserRoleGroupApi(auth);
    const organisationApi = new OrganisationApi(auth);
    const clinicApi = new ClinicApi(auth);
    const userApi = new UserApi(auth);
    const municipalityDistrictApi = new MunicipalityDistrictApi(auth);
    const residenceApi = new ResidenceApi(auth);
    const abortController = new AbortController();
    const userInfo = React.useContext(UserContext);

    const hasRolePrivatAnsvarig = isInRole(MENUETTPORTALKOMMUN_PRIVATANSVARIG, userInfo.roles ? userInfo.roles : []);
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [currentUser, setCurrentUser] = React.useState<IUser>();
    const [applications, setApplications] = React.useState<IListItem[]>([]);
    const [roles, setRoles] = React.useState<ICheckboxListItem[]>();
    const [renderMunicipalitySection, setRenderMunicipalitySection] = React.useState<boolean>(false);
    const [renderRoles, setRenderRoles] = React.useState<boolean>(false);
    const [umbrellaRoleSelected, setUmbrellaRoleSelected] = React.useState<boolean>(false);
    const [privatAnsvarigRoleSelected, setPrivatAnsvarigRoleSelected] = React.useState<boolean>(false);
    const [renderOrganisation, setRenderOrganisation] = React.useState<boolean>(false);
    const [renderClinics, setRenderClinics] = React.useState<boolean>(false);
    const [selectedApplicationId, setSelectedApplicationId] = React.useState<number>(0);
    const [organisations, setOrganisations] = React.useState<IOrganisationClinic[]>();
    const [clinics, setClinics] = React.useState<IOrganisationClinic[]>();
    const [selectedClinicId, setSelectedClinicId] = React.useState<number>();
    const [selectedOrganisationId, setSelectedOrganisationId] = React.useState<number>();
    const [titles, setTitles] = React.useState<IListItem[]>([{ value: "-1", text: "- Ange ny titel -" }]);
    const [renderCustomTitle, setRenderCustomTitle] = React.useState<boolean>(false);
    const [selectedMunicipalityId, setSelectedMunicipalityId] = React.useState<string>();
    const [selectedDistrictId, setSelectedDistrictId] = React.useState<string>();
    const [renderResidenceGroup, setRenderResidenceGroup] = React.useState<boolean>(false);
    const [renderResidence, setRenderResidence] = React.useState<boolean>(false);
    const [residenceGroups, setResidenceGroups] = React.useState<IListItem[]>([]);
    const [residences, setResidences] = React.useState<IListItem[]>([]);
    const [isNewUserSelected, setIsNewUserSelected] = React.useState<boolean>(false);
    const [dateToday] = React.useState<Date>(new Date());
    const [dateYesterday] = React.useState<Date>(new Date(new Date().getTime() - 24 * 60 * 60 * 1000))
    const [dateFrom] = React.useState<Date>(new Date(userRoleGroup?.dateFrom ?? dateToday));
    const [dateTo] = React.useState<Date | undefined>(userRoleGroup?.dateTo ? new Date(userRoleGroup.dateTo) : undefined);
    dateToday.setHours(0, 0, 0, 0);
    dateYesterday.setHours(0, 0, 0, 0);

    // Use effects
    React.useEffect(() => {
        if (organisationId) {
            loadOrganisation(organisationId);
            setSelectedOrganisationId(organisationId);
        }
    }, [organisationId])

    React.useEffect(() => {
        document.getElementById("open-rolegroup-modal")?.click();
        loadApplications();
        loadCurrentUser(userId);

        if (userRoleGroup === undefined && clinicId !== undefined) {
            loadClinic(clinicId);
        }

        if (userRoleGroup) {
            loadRolesForApplication(userRoleGroup.applicationId);
            setRenderRoles(true);

            if (userRoleGroup.applicationId === SYMFONIKOMMUN || userRoleGroup.applicationId === MENUETTPORTALKOMMUN) {
                loadMunicipality();
                loadDistrict();
                loadResidenceGroups();
                setSelectedMunicipalityId(userRoleGroup.municipalityId!.toString());
                if (userRoleGroup.districtId) {
                    setSelectedDistrictId(userRoleGroup.districtId ? userRoleGroup.districtId.toString() : undefined);
                }
                setRenderMunicipalitySection(true);
                setRenderResidenceGroup(userRoleGroup.applicationId === SYMFONIKOMMUN || userRoleGroup.applicationId === MENUETTPORTALKOMMUN);
                setRenderResidence(userRoleGroup.residenceGroupId === SPECIFIKA_BOENDEN);
            }

            if (userRoleGroup.applicationId === SYMFONIVÅRDGIVARE || userRoleGroup.applicationId === LIBRETTOVÅRDGIVARE || userRoleGroup.applicationId === MENUETTPORTAL) {
                loadOrganisations();
                setRenderOrganisation(true);

                if (userRoleGroup.clinicId) {
                    loadClinics(userRoleGroup.organisationId!);
                    setRenderClinics(true);
                }
            }
        }

        return () => {
            abortController.abort();
        }
    }, [userRoleGroup]);

    React.useEffect(() => {
        if (userRoleGroup === undefined) {
            setRenderRoles(false);
            if (selectedApplicationId !== 0 && selectedApplicationId !== LIBRETTOVÅRDGIVARE && selectedApplicationId !== SYMFONIVÅRDGIVARE && selectedApplicationId && selectedApplicationId !== MENUETTPORTAL) {
                loadRolesForApplication(selectedApplicationId);
            }

            if (selectedApplicationId === SYMFONIKOMMUN || selectedApplicationId === MENUETTPORTALKOMMUN) {
                loadMunicipality();
                loadDistrict();
            }

            if (clinicId === undefined) {
                setSelectedOrganisationId(undefined);
                setSelectedClinicId(undefined);
            }
            setUmbrellaRoleSelected(false);
            setPrivatAnsvarigRoleSelected(false);
            setRenderOrganisation(false);
            setOrganisations(undefined);
            setClinics(undefined);
            setRenderCustomTitle(false);
            setRenderResidenceGroup(selectedApplicationId === SYMFONIKOMMUN || selectedApplicationId === MENUETTPORTALKOMMUN);
            if (selectedApplicationId === SYMFONIKOMMUN || selectedApplicationId === MENUETTPORTALKOMMUN) {
                loadResidenceGroups();
            }
            setRenderResidence(false);
            setRenderClinics(false);
            setRenderMunicipalitySection(selectedApplicationId === SYMFONIKOMMUN || selectedApplicationId === MENUETTPORTALKOMMUN);

            if (clinicId === undefined && organisationId === undefined) {
                if (selectedApplicationId === LIBRETTOVÅRDGIVARE || selectedApplicationId === SYMFONIVÅRDGIVARE || selectedApplicationId === MENUETTPORTAL) {
                    setRenderOrganisation(true);
                    loadOrganisations();
                }
            } else {
                loadRolesForRoleRequest(buildRoleRequest());
            }

        }

    }, [selectedApplicationId])

    React.useEffect(() => {
        if (!userRoleGroup) {
            if (selectedApplicationId && selectedOrganisationId && !isNaN(selectedApplicationId)) {
                setRenderClinics(!umbrellaRoleSelected);
            }
        }

    }, [umbrellaRoleSelected]);

    React.useEffect(() => {
        if (!userRoleGroup) {
            setRenderResidence(false);
            if (privatAnsvarigRoleSelected) {
                setRenderResidence(true);
                loadResidencesForMunicipalityDistrict();
            }
        }

    }, [privatAnsvarigRoleSelected]);

    React.useEffect(() => {
        if (clinicId === undefined) {
            if (userRoleGroup?.userRoleGroupId == undefined) {
                loadRolesForRoleRequest(buildRoleRequest());
            }
        }
    }, [selectedClinicId]);

    React.useEffect(() => {
        setResidences([]);
        if (selectedMunicipalityId) {
            loadTitles();
            loadResidencesForMunicipalityDistrict();
        }
    }, [selectedMunicipalityId, selectedDistrictId])

    // Api
    const loadApplications = () => {
        setIsLoading(true);

        api.getApplications((data) => {
            let mappedApplications = data.map<IListItem>((application) => { return { text: application.applicationName, value: application.applicationId.toString() } });
            if (clinicId || organisationId) {
                mappedApplications = mappedApplications.filter((application) => parseInt(application.value) === LIBRETTOVÅRDGIVARE || parseInt(application.value) === SYMFONIVÅRDGIVARE || parseInt(application.value) === MENUETTPORTAL);
            }
            if (openedFromMunicipality) {
                mappedApplications = mappedApplications.filter((application) => parseInt(application.value) === SYMFONIKOMMUN || parseInt(application.value) == MENUETTPORTALKOMMUN);
                if (hasRolePrivatAnsvarig) {
                    mappedApplications = mappedApplications.filter((application) => parseInt(application.value) === SYMFONIKOMMUN);
                }
            }
            setApplications(mappedApplications);
        }, undefined, () => setIsLoading(false));
    }

    const loadCurrentUser = (userId?: number) => {
        if (userId) {
            setIsLoading(true);
            userApi.getUserByUserId(userId, (data) => {
                setCurrentUser(data);
            }, undefined, () => setIsLoading(false));
        }
    }

    const loadRolesForApplication = (applicationId: number) => {
        setIsLoading(true);
        setRenderRoles(false);
        api.getRolesByApplicationId(applicationId, (data) => {
            let mappedRoles = data.map<ICheckboxListItem>((role) => { return { id: role.roleId, name: role.roleName, description: role.roleDescription, invalidCombinationIds: role.invalidCombinationRoleIds } });
            setRoles(mappedRoles);
            setRenderRoles(true);
        }, undefined, () => setIsLoading(false));
    }

    const loadRolesForRoleRequest = (roleRequest: IRoleRequest) => {
        setIsLoading(true);
        setRenderRoles(false);
        api.getRolesByRoleRequest(roleRequest, (data) => {
            let mappedRoles = data.map<ICheckboxListItem>((role) => { return { id: role.roleId, name: role.roleName, description: role.roleDescription, invalidCombinationIds: role.invalidCombinationRoleIds } });
            setRoles(mappedRoles);
            setRenderRoles(true);
        }, undefined, () => setIsLoading(false));
    }

    const loadOrganisations = () => {
        setIsLoading(true);
        organisationApi.getOrganisationsListItems((data) => {
            setOrganisations(data);
        }, undefined, () => setIsLoading(false));
    }

    const loadOrganisation = (organisationId: number) => {
        organisationApi.getOrganisationById(organisationId, (data) => {
            setSelectedOrganisationId(data.organisationId);
        }, undefined, undefined)
    }

    const loadClinics = (organisationId: number | undefined) => {
        if (organisationId) {
            setIsLoading(true);
            clinicApi.getClinicsListItems(organisationId, (data) => {
                setClinics(data);
            }, undefined, () => setIsLoading(false));
        }
    }

    const loadClinic = (clinicId: number) => {
        clinicApi.getClinicByClinicId(clinicId, (data) => {
            setSelectedOrganisationId(data.organisationId);
            setSelectedClinicId(data.clinicId);
        }, undefined, undefined)
    }

    const loadMunicipality = () => {
        setIsLoading(true);
        municipalityDistrictApi.getMunicipality((result) => {
            setSelectedMunicipalityId(result.municipalityId.toString());
        }, (error) => { setErrorMessage(error) }, () => { setIsLoading(false); loadDistrict(); });
    }

    const loadDistrict = () => {
        setIsLoading(true);
        municipalityDistrictApi.getDistrict((result) => {
            if (result.districtCode === UNKOWNDISTRICTCODE) {
                setSelectedDistrictId(undefined);
            } else {
                setSelectedDistrictId(result.districtId.toString());
            }
        }, (error) => { setErrorMessage(error) }, () => { setIsLoading(false); });
    }

    const loadTitles = () => {
        setIsLoading(true);
        municipalityDistrictApi.getTitles((data) => {
            const mappedTitles = data.map<IListItem>((title) => { return { text: title, value: title } });
            setTitles(titles.concat(mappedTitles));
        }, undefined, () => setIsLoading(false));
    }

    const loadResidenceGroups = () => {
        setIsLoading(true);
        residenceApi.getResidenceGroups((data) => {
            const mappedResidenceGroups = data.map<IListItem>((residenceGroup) => { return { text: residenceGroup.residenceGroupName, value: residenceGroup.residenceGroupId.toString() } });
            setResidenceGroups(mappedResidenceGroups);
        })
    }

    const loadResidencesForRegistration = () => {
        if (selectedMunicipalityId) {
            setIsLoading(true);

            var searchParams: IResidenceSearchParams = { searchType: ISearchType.Residence, pageSize: 10000, pageNumber: 1, propertyToOrderBy: 'ResidenceCode', sortOrder: SortOrder.ascending }

            residenceApi.searchResidences(searchParams, (result) => {
                const mappedResidences = result.data.residences.map<IListItem>((residence) => { return { text: `(${residence.residenceCode}) ${residence.residenceName}`, value: residence.residenceId.toString() } });
                setResidences(mappedResidences);
            }, () => setIsLoading(false));
        }
    }

    const loadResidencesForMunicipalityDistrict = () => {
        if (selectedMunicipalityId) {
            setIsLoading(true);

            const municipalityDistrict: IMunicipalityDistrict = {
                municipalityId: parseInt(selectedMunicipalityId),
                districtId: selectedDistrictId ? parseInt(selectedDistrictId) : undefined
            };

            residenceApi.getResidencesByMunicipalityDistrict(municipalityDistrict, (data) => {
                const mappedResidences = data.map<IListItem>((residence) => { return { text: `(${residence.residenceCode}) ${residence.residenceName}`, value: residence.residenceId.toString() } });
                setResidences(mappedResidences);
            }, undefined, () => setIsLoading(false));
        }
    }

    // Events
    const onApplicationChange = (applicationId: string) => {
        setSelectedApplicationId(parseInt(applicationId));
    }

    const onClinicChange = (clinicId: string) => {
        setSelectedClinicId(clinicId ? parseInt(clinicId) : undefined);
    }

    const onTitleChange = (value: string) => {
        setRenderCustomTitle(value === "-1");
    }

    const onResidenceGroupChange = (value: string) => {
        setRenderResidence(false);
        const residentGroupId = parseInt(value);

        if (residentGroupId === SPECIFIKA_BOENDEN) {
            setRenderResidence(true);
            loadResidencesForRegistration();
        }
    }

    const onRoleSelect = (selectedRoleIds: string[]) => {
        setPrivatAnsvarigRoleSelected(selectedRoleIds.includes(MENUETTPORTALKOMMUN_PRIVATANSVARIG_ROLEID));
    }

    const onFormSubmit = (data: IUserRoleGroup) => {
        setIsLoading(true);
        data.userRoleGroupId = parseInt(data.userRoleGroupId.toString());
        data.title = data.customTitle ?? data.title;
        data.roleIds = userRoleGroup ? userRoleGroup.roleIds : data.roleIds ? data.roleIds : [];

        if (!data.organisationId && selectedOrganisationId) {
            data.organisationId = selectedOrganisationId;
        }
        if (!data.clinicId && selectedClinicId) {
            data.clinicId = selectedClinicId;
        }
        if (!data.userId && currentUser) {
            data.userId = currentUser.userId;
        }
        if (!data.municipalityId && selectedMunicipalityId) {
            data.municipalityId = parseInt(selectedMunicipalityId);
        }
        if (!data.districtId && selectedDistrictId) {
            data.districtId = parseInt(selectedDistrictId);
        }

        if (!userRoleGroup && data.applicationId == MENUETTPORTALKOMMUN) {
            data.residenceGroupId = privatAnsvarigRoleSelected ? SPECIFIKA_BOENDEN : ALLABOENDEN;
        }

        if (data.userRoleGroupId === 0) {
            api.getCreateUserRoleGroupImpactMessage(data, message => {
                if (message && message !== "") {
                    const createConfirm = window.confirm(message);

                    if (createConfirm) {
                        api.createUserRoleGroup(data, (createdUserRoleGroupId) => {
                            onSubmit(true);
                        }, (message: string) => {
                            const newMessage = setMessage(message, errorMessage);
                            setErrorMessage(newMessage);
                        }, () => setIsLoading(false));
                    } else {
                        setIsLoading(false);
                        return;
                    }
                } else {
                    api.createUserRoleGroup(data, (createdUserRoleGroupId) => {
                        onSubmit(true);
                    }, (message: string) => {
                        const newMessage = setMessage(message, errorMessage);
                        setErrorMessage(newMessage);
                    }, () => setIsLoading(false));
                }
            });
        } else {
            api.updateUserRoleGroup(data, () => {
                onSubmit(false);
            }, (message: string) => {
                const newMessage = setMessage(message, errorMessage);
                setErrorMessage(newMessage);
            }, () => setIsLoading(false));
        }
    }

    const getOrganisationDisplayValue = () => {
        if (!userRoleGroup || organisations === undefined) {
            return '';
        }

        const organisation = organisations.find(
            (organisation) => organisation.id === userRoleGroup.organisationId
        );

        return organisation ? `${organisation.organisationNumber} - ${organisation.name}` : '';
    };

    const getClinicDisplayValue = () => {
        if (!userRoleGroup || clinics === undefined) {
            return '';
        }

        const clinic = clinics.find(
            (clinic) => clinic.id === userRoleGroup.clinicId
        );

        return clinic ? `${clinic.clinicNumber} - ${clinic.name}` : '';
    };

    const renderSendWelcomeEmailCheckbox = () => {
        return selectedApplicationId === LIBRETTOVÅRDGIVARE || selectedApplicationId === SYMFONIVÅRDGIVARE || selectedApplicationId === SYMFONIKOMMUN || selectedApplicationId === MENUETTPORTAL || selectedApplicationId === MENUETTPORTALKOMMUN
    };

    const buildRoleRequest = () => {
        const dateFromInput = document.getElementById('dateFrom') as HTMLInputElement;
        const dateToInput = document.getElementById('dateTo') as HTMLInputElement;

        const dateFrom = new Date(dateFromInput.value).toLocaleDateString("sv-se");
        const dateTo = dateToInput.value ? new Date(dateToInput.value).toLocaleDateString("sv-se") : undefined;

        const roleRequest: IRoleRequest = {
            applicationId: selectedApplicationId,
            dateFrom: dateFrom,
            dateTo: dateTo,
            organisationId: organisationId !== undefined ? organisationId : selectedOrganisationId,
            clinicId: selectedClinicId
        }

        return roleRequest;
    }

    const currentUserFullName = currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : '';
    const currentUserFullNameHsaId = currentUser ? `${currentUser.hsaId} - ${currentUser.firstName} ${currentUser.lastName}` : '';

    const renderUserField = () => {
        if (currentUser && openedFromUser) {
            return (
                <TextInput labelCol={4} inputCol={8} label="Användare" name="readonly" defaultValue={currentUserFullName} readonly inlineLabel />
            );
        } else if (currentUser && openedFromClinic && userId === undefined) {
            return (
                <>
                    <CustomButton buttonText="Sök upp ny användare" buttonType={ButtonType.edit} onClick={() => setCurrentUser(undefined)} />
                    <TextInput labelCol={4} inputCol={8} label="Användare" name="readonly" defaultValue={currentUserFullNameHsaId} readonly inlineLabel />
                </>
            );
        } else if (currentUser) {
            return (
                <TextInput labelCol={4} inputCol={8} label="Användare" name="readonly" defaultValue={currentUserFullNameHsaId} readonly inlineLabel />
            );
        } else if (currentUser === undefined) {
            return (
                <>
                    {
                        !isNewUserSelected ?
                            <SearchUser name="searchUser" label="Användare" placeholder="Sök användare..." onChange={(val: string) => loadCurrentUser(parseInt((val)))} errorMessage="Användare måste anges" required />
                            : null
                    }
                    <Checkbox name="addNew" label="Ny användare" value="true" onChange={onNewUserChange} />
                    {
                        isNewUserSelected ?
                            <>
                                <TextInput label="Förnamn" name="newUser.userFirstName" required inlineLabel />
                                <TextInput label="Efternamn" name="newUser.userLastName" required inlineLabel />
                                <TextInput label="Telefonnummer" name="newUser.userPhoneNumber" inlineLabel />
                                <TextInput label="Mobiltelefonnummer" name="newUser.userMobilePhoneNumber" inlineLabel />
                                <HsaIdInput label="HSA-ID" name="newUser.userHsaId" required inlineLabel />
                                <TextInput label="E-postadress" name="newUser.userEmailAddress" maxLength={50} pattern={emailAddressRegExp()} patternValidationMessage="Ogiltigt format på e-postadress" required inlineLabel />
                            </> : null
                    }

                </>
            );
        }
        return null;
    }

    const onNewUserChange = (checked: boolean) => {
        setIsNewUserSelected(checked);
    }

    const renderOrganisationField = () => {
        return (
            <TextInput labelCol={4} inputCol={8} label="Vårdgivare" name="readonly" defaultValue={getOrganisationDisplayValue()} readonly inlineLabel />
        );
    }

    const renderClinicField = () => {
        return (
            <TextInput labelCol={4} inputCol={8} label="Mottagning" name="readonly" defaultValue={getClinicDisplayValue()} readonly inlineLabel />
        );
    }

    const getClinicItemLabel = (organisationClinic: IOrganisationClinic) => {
        return organisationClinic.clinicNumber ? `${organisationClinic.clinicNumber} - ${organisationClinic.name}` : organisationClinic.name;
    }

    const getSelectedResidenceGroupId = () => {
        if (selectedApplicationId && selectedApplicationId === MENUETTPORTALKOMMUN) {
            return privatAnsvarigRoleSelected ? SPECIFIKA_BOENDEN.toString() : ALLABOENDEN.toString();
        } else {
            return "";
        }
    }

    return (
        <Modal id="rolegroup-modal" modalSize={ModalSize.extraLarge} header={userRoleGroup?.userRoleGroupId ? canEdit ? "Redigera rollgrupp" : "Visa rollgrupp" : "Lägg till rollgrupp"}>
            <Form onSubmit={onFormSubmit}>
                <ModalBody>
                    <AlertMessage alertType={AlertType.danger} duration={5000} >{errorMessage}</AlertMessage>
                    <div className="row">
                        <div className="col-6">
                            <HiddenInput name="userRoleGroupId" value={userRoleGroup?.userRoleGroupId.toString() ?? "0"} />
                            <HiddenInput name="userId" value={userId ? userId.toString() : currentUser ? currentUser.userId.toString() : "0"} />
                            {renderUserField()}
                            {
                                canEdit ?
                                    <DatePickerRange label="Fr.o.m. - t.o.m."
                                        name="dateFrom"
                                        value={dateFrom}
                                        valueSecondary={dateTo}
                                        nameSecondary="dateTo"
                                        min={userRoleGroup ? (new Date(userRoleGroup.dateFrom) > dateToday ? dateToday : new Date(userRoleGroup.dateFrom)) : dateToday}
                                        minSecondary={!userRoleGroup ? undefined : new Date(userRoleGroup.dateFrom) > dateYesterday ? new Date(userRoleGroup.dateFrom) : dateYesterday}
                                        disabledFrom={userRoleGroup && new Date(userRoleGroup.dateFrom) < dateToday}
                                        disabledTo={userRoleGroup && userRoleGroup.dateTo ? new Date(userRoleGroup.dateTo) < dateToday : false}
                                        requiredFrom
                                        inlineLabel />
                                    :
                                    <TextInput labelCol={4} inputCol={8} label="Fr.o.m. - t.o.m." name="readonly" defaultValue={getStartAndEndDateDisplayValue(userRoleGroup?.dateFrom, userRoleGroup?.dateTo ? userRoleGroup?.dateTo : undefined)} readonly inlineLabel />
                            }
                            {
                                userRoleGroup ?
                                    <>
                                        <HiddenInput name="applicationId" value={userRoleGroup.applicationId.toString()} />
                                        <TextInput labelCol={4} inputCol={8} label="IT-system" name="readonly" defaultValue={applications.find((application) => application.value === userRoleGroup.applicationId.toString())?.text} readonly inlineLabel />
                                    </>
                                    :
                                    <Select name="applicationId" label="IT-system" placeholder="Välj ett IT-system" options={applications} required inlineLabel disabled={userRoleGroup !== undefined} onChange={onApplicationChange} />
                            }
                            {
                                renderMunicipalitySection ?
                                    <>
                                        {
                                            userRoleGroup ?
                                                <TextInput labelCol={4} inputCol={8} label="Titel" name="readonly" defaultValue={userRoleGroup.title!} readonly inlineLabel />
                                                : <Select labelCol={4} inputCol={8} name="title" label="Titel" placeholder="Välj en titel" options={titles} onChange={onTitleChange} required inlineLabel />
                                        }
                                        {
                                            renderCustomTitle ?
                                                <TextInput labelCol={4} inputCol={8} placeholder="Ange en ny titel" label="Ny titel" name="customTitle" defaultValue={userRoleGroup?.title ? userRoleGroup.title : ""} required inlineLabel />
                                                : null
                                        }
                                        {
                                            renderResidenceGroup ?
                                                <>
                                                    {
                                                        userRoleGroup ?
                                                            <TextInput labelCol={4} inputCol={8} label="Boendetyp" name="readonly" defaultValue={residenceGroups!.find((residenceGroup) => residenceGroup.value === userRoleGroup.residenceGroupId?.toString())?.text} readonly inlineLabel />
                                                            :
                                                            <Select isClearable={parseInt(selectedApplicationId.toString()) !== parseInt(MENUETTPORTALKOMMUN.toString())} labelCol={4} inputCol={8} isLoading={isLoading} placeholder="Välj boendetyp" selectedValue={getSelectedResidenceGroupId()} name="residenceGroupId" label="Boendetyp" options={residenceGroups ?? []} onChange={onResidenceGroupChange} required disabled={selectedApplicationId === MENUETTPORTALKOMMUN} inlineLabel />
                                                    }
                                                </>
                                                : null
                                        }
                                        {
                                            renderResidence ?
                                                <>
                                                    {
                                                        userRoleGroup ? (
                                                            <UnorderedList
                                                                name="residenceIds"
                                                                textRows={userRoleGroup.residenceIds
                                                                    .map((r) => residences.find((residence) => residence.value === r.toString())?.text)
                                                                    .filter((name) => name !== undefined) as string[]}
                                                                label="Boendeenheter"
                                                                inlineLabel
                                                            />
                                                        ) : (
                                                            <MultiSelect
                                                                labelCol={4}
                                                                inputCol={8}
                                                                name="residenceIds"
                                                                placeholder="Välj boendeenhet(er)"
                                                                label="Boendeenheter"
                                                                values={residences}
                                                                isMultiple
                                                            />
                                                        )
                                                    }
                                                </>
                                                : null
                                        }
                                    </>
                                    : null
                            }
                            {
                                renderOrganisation && organisations && !openedFromOrganisation && !openedFromClinic ?
                                    <>
                                        {
                                            userRoleGroup && userRoleGroup.organisationId ?
                                                <>
                                                    {renderOrganisationField()}
                                                </>
                                                :
                                                null
                                        }
                                        {
                                            renderClinics ?
                                                <>
                                                    {
                                                        userRoleGroup && userRoleGroup.clinicId ?
                                                            <>
                                                                {renderClinicField()}
                                                            </>
                                                            :
                                                            <Dropdown name="clinicId" label="Mottagning" clearValueIfNoInitalValue items={clinics} getItemLabel={getClinicItemLabel} placeholder="Välj en mottagning" onValueChange={onClinicChange} noOptionsMessage="Det finns inga valbara mottagningar" defaultValue={undefined} errorMessage="Välj en mottagning" isLoading={isLoading} isClearable={true} required />
                                                    }
                                                </>
                                                : null
                                        }
                                    </>
                                    :
                                    <>
                                        <HiddenInput name="organisationId" value={userRoleGroup?.organisationId?.toString() ?? organisationId?.toString() ?? ""} />
                                        <HiddenInput name="clinicId" value={userRoleGroup?.clinicId?.toString() ?? clinicId?.toString() ?? ""} />
                                    </>
                            }
                            {
                                renderSendWelcomeEmailCheckbox() ?
                                    <Checkbox
                                        label="Meddela via e-post:"
                                        name="sendEmail"
                                        checked={true}
                                        labelCol={4}
                                        inputCol={8}
                                        value="true"
                                        inlineLabel
                                        id="sendEmail"
                                    />
                                    : null
                            }
                        </div>
                        <div className="col-6">
                            {
                                renderRoles && roles && roles.length > 0 ?
                                    <ConditionalCheckboxList name="roleIds" label="Roller" onSelect={onRoleSelect} required={userRoleGroup === undefined} items={roles} existingItemIds={userRoleGroup ? userRoleGroup.roleIds : undefined} labelCol={11} inputCol={1} />
                                    :
                                    selectedApplicationId ?
                                        organisationId ?
                                            <p>Vårdgivaren saknar giltligt avtalsområde...</p>
                                            :
                                            <p>Vårdgivare eller mottagning saknar ett med giltligt avtal- eller vårdområde för valt IT-system</p>
                                        :
                                        <p>Välj IT-system...</p>
                            }
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="text-right btn-group" role="group">
                        <CustomButton buttonType={ButtonType.general} id="close-rolegroup-modal" data-dismiss="modal" onClick={() => closeModal()} buttonText="Avbryt" />
                        {
                            canEdit ?
                                <CustomSubmitButton isLoading={isLoading}>Spara</CustomSubmitButton>
                                : null
                        }

                        <input type="hidden" data-toggle="modal" id="open-rolegroup-modal" data-target="#rolegroup-modal" />
                    </div>
                </ModalFooter>
            </Form>
        </Modal>);
}